<template>
  <article class="audio-entry">
    <div class="audio-art" aria-hidden="true">
      <v-icon size="48">mdi-waveform</v-icon>
      <span>VOICE MEMORY</span>
    </div>

    <div class="audio-body">
      <h3 v-if="media.title" class="audio-title">{{ media.title }}</h3>
      <audio :src="mediaUrl" controls preload="metadata"></audio>

      <div v-if="transcript" class="transcript-wrap">
        <p class="transcript-label">Transcript</p>
        <p class="transcript" :class="{ collapsed: !expanded }">{{ transcript }}</p>
        <button v-if="hasLongTranscript" type="button" class="see-more" @click="expanded = !expanded">
          {{ expanded ? 'Show less' : 'See more' }}
        </button>
      </div>
      <!--
      <p v-else-if="isTranscribing" class="transcript-pending" aria-live="polite">
        Transcript is not ready yet.
      </p>
      -->
    </div>
  </article>
</template>

<script setup>
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import apiService from '../../services/api'

const props = defineProps({
  media: {
    type: Object,
    required: true
  },
  memorialSlug: {
    type: String,
    default: ''
  }
})

const expanded = ref(false)
const transcript = ref(props.media.transcript || '')
const transcriptionStatus = ref(props.media.transcriptionStatus || '')
let pollTimer = null

const mediaUrl = computed(() => {
  if (props.media.url) return props.media.url
  if (props.media.location) return `${props.media.location}/${props.media.filename}.${props.media.extension}`
  return `https://assets.forever-x.com/photos/${props.media.filename}.${props.media.extension}`
})

const isTranscribing = computed(() => ['PENDING', 'PROCESSING'].includes(transcriptionStatus.value))
const hasLongTranscript = computed(() => transcript.value.length > 220)

const stopPolling = () => {
  if (pollTimer) clearTimeout(pollTimer)
  pollTimer = null
}

const schedulePoll = () => {
  stopPolling()
  if (!props.memorialSlug || !props.media.uuid || !isTranscribing.value) return
  pollTimer = setTimeout(pollForTranscript, 10000)
}

const pollForTranscript = async () => {
  try {
    const response = await apiService.getMediaTranscription(props.memorialSlug, props.media.uuid)
    transcript.value = response.transcript || ''
    transcriptionStatus.value = response.transcriptionStatus || ''
  } catch (_) {
    // A temporary polling failure should not interfere with audio playback.
  }
  schedulePoll()
}

watch(
  () => [props.media.uuid, props.media.transcript, props.media.transcriptionStatus, props.memorialSlug],
  () => {
    transcript.value = props.media.transcript || ''
    transcriptionStatus.value = props.media.transcriptionStatus || ''
    expanded.value = false
    schedulePoll()
  },
  { immediate: true }
)

onBeforeUnmount(stopPolling)
</script>

<style scoped>
.audio-entry {
  display: grid;
  grid-template-columns: minmax(150px, 28%) minmax(0, 1fr);
  overflow: hidden;
  min-height: 178px;
  border: 1px solid rgba(84, 56, 51, 0.14);
  border-radius: 18px;
  background: white;
  box-shadow: 0 8px 28px rgba(61, 42, 38, 0.08);
}

.audio-art {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  min-height: 178px;
  padding: 24px;
  color: #fff;
  background:
    radial-gradient(circle at 20% 20%, rgba(255, 255, 255, 0.22), transparent 36%),
    linear-gradient(145deg, #a85a51, #6b332e);
}

.audio-art span {
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.15em;
}

.audio-body {
  padding: 22px 24px;
}

.audio-title {
  margin: 0 0 12px;
  color: #3f302e;
  font-size: 1.05rem;
}

.audio-body audio {
  width: 100%;
  max-width: 620px;
}

.transcript-wrap {
  margin-top: 18px;
}

.transcript-label {
  margin: 0 0 5px;
  color: #8a514a;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.transcript {
  margin: 0;
  color: #514542;
  line-height: 1.65;
  white-space: pre-wrap;
}

.transcript.collapsed {
  display: -webkit-box;
  overflow: hidden;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
}

.see-more {
  margin-top: 6px;
  padding: 0;
  border: 0;
  color: #914b43;
  background: transparent;
  font-weight: 700;
  cursor: pointer;
}

.transcript-pending {
  display: flex;
  align-items: center;
  margin: 16px 0 0;
  color: #746865;
  font-size: 0.88rem;
}

@media (max-width: 600px) {
  .audio-entry {
    grid-template-columns: 96px minmax(0, 1fr);
  }

  .audio-art {
    min-height: 160px;
    padding: 16px 8px;
  }

  .audio-art span {
    font-size: 0.55rem;
    text-align: center;
  }

  .audio-body {
    padding: 18px 14px;
  }
}
</style>
